import React, { useState } from 'react';
import { useAuth } from '../../authContext';

// Shared Components importieren
import Modal from '../shared/Modal';
import StatusBadge from '../shared/StatusBadge';
import SearchBar from '../shared/SearchBar';
import DataTable from '../shared/DataTable';
import ConfirmModal from '../shared/ConfirmModal';
import CompanyForm from '../companies/CompanyForm';

// Custom Hook importieren
import { useCompanies } from '../companies/useCompanies';

const Companies = () => {
  // Hooks
  const { role } = useAuth();
  const {
    companies,
    loading,
    error,
    toggleCompanyField,
    addCompany,
    editCompany,
    deactivateCompany,
  } = useCompanies();

  // Modal-State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Formular- und Auswahl-State
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedCompanyForToggle, setSelectedCompanyForToggle] = useState(null);
  const [newCompany, setNewCompany] = useState({
    name: '',
    sector: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    remarks: '',
    highlighted: false,
    contacted: false,
    verified: false,
    isExtern: false,
  });
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewCompany, setViewCompany] = useState(null);

  // Filter-State
  const [searchName, setSearchName] = useState('');
  const [searchIndustry, setSearchIndustry] = useState('');
  const [searchAddress, setSearchAddress] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [highlightedFilter, setHighlightedFilter] = useState('all');
  const [contactedFilter, setContactedFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [isExternFilter, setIsExternFilter] = useState('all');

  // --- Filterlogik ---
  const filteredCompanies = companies.filter((company) => {
    const nameMatch = company.name.toLowerCase().includes(searchName.toLowerCase());
    const industryMatch = company.sector.toLowerCase().includes(searchIndustry.toLowerCase());
    const addressMatch = company.address.toLowerCase().includes(searchAddress.toLowerCase());
    const statusMatch =
      statusFilter === 'all' ||
      (statusFilter === 'active' && company.active) ||
      (statusFilter === 'inactive' && !company.active);
    const highlightedMatch =
      highlightedFilter === 'all' ||
      (highlightedFilter === 'yes' && company.highlighted) ||
      (highlightedFilter === 'no' && !company.highlighted);
    const contactedMatch =
      contactedFilter === 'all' ||
      (contactedFilter === 'yes' && company.contacted) ||
      (contactedFilter === 'no' && !company.contacted);
    const verifiedMatch =
      verifiedFilter === 'all' ||
      (verifiedFilter === 'yes' && company.verified) ||
      (verifiedFilter === 'no' && !company.verified);
    const isExternMatch =
      isExternFilter === 'all' ||
      (isExternFilter === 'yes' && company.isExtern) ||
      (isExternFilter === 'no' && !company.isExtern);
    return nameMatch && industryMatch && addressMatch && statusMatch && highlightedMatch && contactedMatch && verifiedMatch && isExternMatch;
  });

  // --- Hilfsfunktion zum Parsen der Adresse ---
  const parseAddress = (address) => {
    const parts = address ? address.split(',').map(part => part.trim()) : ['', ''];
    if (parts.length === 2) {
      const [street, postalCity] = parts;
      const postalCityParts = postalCity.split(' ').filter(Boolean);
      const postal_code = postalCityParts.length >= 1 ? postalCityParts[0] : '';
      const city = postalCityParts.length >= 2 ? postalCityParts.slice(1).join(' ') : '';
      return { street, postal_code, city };
    }
    return { street: address || '', postal_code: '', city: '' };
  };

  // --- CSV-Export-Funktion ---
  const exportToCSV = () => {
    const headers = ['name', 'sector', 'address', 'phone', 'email', 'remarks', 'street', 'postal_code', 'city'];

    const csvContent = [
      headers.join(';'),
      ...filteredCompanies.map(company => {
        const { street, postal_code, city } = parseAddress(company.address);
        return [
          company.name || '',
          company.sector || '',
          company.address || '',
          company.phone || '',
          company.email || '',
          company.remarks || '',
          street,
          postal_code,
          city,
        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(';');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `unternehmen_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- Handler ---
  const openToggleStatusModal = (companyId, currentStatus) => {
    setSelectedCompanyForToggle({ id: companyId, currentStatus });
    setShowToggleStatusModal(true);
  };

  const handleToggleStatus = async (companyId, currentStatus) => {
    try {
      await toggleCompanyField(companyId, 'active', currentStatus);
      setShowToggleStatusModal(false);
    } catch (err) {
      setErrorModalMessage(err.message);
      setShowErrorModal(true);
      setShowToggleStatusModal(false);
    }
  };

  const handleToggleHighlighted = async (companyId, currentHighlighted) => {
    try {
      await toggleCompanyField(companyId, 'highlighted', currentHighlighted);
    } catch (err) {
      setErrorModalMessage('Fehler beim Ändern des Highlighted-Status.');
      setShowErrorModal(true);
    }
  };

  const handleToggleContacted = async (companyId, currentContacted) => {
    try {
      await toggleCompanyField(companyId, 'contacted', currentContacted);
    } catch (err) {
      setErrorModalMessage('Fehler beim Ändern des Kontaktiert-Status.');
      setShowErrorModal(true);
    }
  };

  const handleToggleVerified = async (companyId, currentVerified) => {
    try {
      await toggleCompanyField(companyId, 'verified', currentVerified);
    } catch (err) {
      setErrorModalMessage('Fehler beim Ändern des Verifiziert-Status.');
      setShowErrorModal(true);
    }
  };

  const handleToggleIsExtern = async (companyId, currentIsExtern) => {
    try {
      await toggleCompanyField(companyId, 'isExtern', currentIsExtern);
    } catch (err) {
      setErrorModalMessage('Fehler beim Ändern des Extern-Status.');
      setShowErrorModal(true);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    try {
      await addCompany(newCompany);
      setShowAddModal(false);
      setNewCompany({
        name: '',
        sector: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        remarks: '',
        highlighted: false,
        contacted: false,
        verified: false,
        isExtern: false,
      });
    } catch (err) {
      setErrorModalMessage('Fehler beim Erstellen des Unternehmens.');
      setShowErrorModal(true);
    }
  };

  const handleEditCompany = async (e) => {
    e.preventDefault();
    try {
      await editCompany(editingCompany.id, newCompany);
      setShowEditModal(false);
    } catch (err) {
      setErrorModalMessage('Fehler beim Bearbeiten des Unternehmens.');
      setShowErrorModal(true);
    }
  };

  const handleDeactivateCompany = async (companyId) => {
    try {
      await deactivateCompany(companyId);
      setShowDeactivateModal(false);
    } catch (err) {
      setErrorModalMessage('Fehler beim Deaktivieren des Unternehmens.');
      setShowErrorModal(true);
    }
  };

  // --- Suchfelder für SearchBar ---
  const searchFields = [
    { type: 'text', placeholder: 'Suche nach Name...', value: searchName, onChange: setSearchName },
    { type: 'text', placeholder: 'Suche nach Branche...', value: searchIndustry, onChange: setSearchIndustry },
    { type: 'text', placeholder: 'Suche nach Adresse...', value: searchAddress, onChange: setSearchAddress },
    {
      type: 'select',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'Alle Status' },
        { value: 'active', label: 'Aktiv' },
        { value: 'inactive', label: 'Inaktiv' },
      ],
    },
    {
      type: 'select',
      value: contactedFilter,
      onChange: setContactedFilter,
      options: [
        { value: 'all', label: 'Alle Kontakte' },
        { value: 'yes', label: 'Kontaktiert' },
        { value: 'no', label: 'Nicht kontaktiert' },
      ],
    },
    {
      type: 'select',
      value: highlightedFilter,
      onChange: setHighlightedFilter,
      options: [
        { value: 'all', label: 'Alle Highlights' },
        { value: 'yes', label: 'Hervorgehoben' },
        { value: 'no', label: 'Nicht hervorgehoben' },
      ],
    },
    {
      type: 'select',
      value: verifiedFilter,
      onChange: setVerifiedFilter,
      options: [
        { value: 'all', label: 'Alle Verifiziert' },
        { value: 'yes', label: 'Verifiziert' },
        { value: 'no', label: 'Nicht verifiziert' },
      ],
    },
    {
      type: 'select',
      value: isExternFilter,
      onChange: setIsExternFilter,
      options: [
        { value: 'all', label: 'Alle Ext. Vertrieb' },
        { value: 'yes', label: 'Extern' },
        { value: 'no', label: 'Nicht extern' },
      ],
    },
  ];

  // --- Tabellen-Spalten für DataTable ---
  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'sector', header: 'Branche' },
    { key: 'email', header: 'E-Mail' },
    { key: 'phone', header: 'Telefon', style: { width: '120px' } },
    {
      key: 'active',
      header: 'Status',
      render: (company) => (
        <StatusBadge
          value={company.active}
          onClick={() => openToggleStatusModal(company.id, company.active)}
          type="status"
        />
      ),
    },
    {
      key: 'contacted',
      header: 'Kont.',
      render: (company) => (
        <StatusBadge
          value={company.contacted}
          onClick={() => handleToggleContacted(company.id, company.contacted)}
          type="contacted"
        />
      ),
    },
    {
      key: 'highlighted',
      header: 'Highl.',
      render: (company) => (
        <StatusBadge
          value={company.highlighted}
          onClick={() => handleToggleHighlighted(company.id, company.highlighted)}
          type="highlighted"
        />
      ),
    },
    {
      key: 'verified',
      header: 'Ver.',
      render: (company) => (
        <StatusBadge
          value={company.verified}
          onClick={() => handleToggleVerified(company.id, company.verified)}
          type="verified"
        />
      ),
    },
    {
      key: 'isExtern',
      header: 'Ext.',
      render: (company) => (
        <StatusBadge
          value={company.isExtern}
          onClick={() => handleToggleIsExtern(company.id, company.isExtern)}
          type="isExtern"
        />
      ),
    },
    {
      key: 'actions',
      header: 'Aktionen',
      render: (company) => (
        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            className="action-button view"
            onClick={() => {
              setViewCompany(company);
              setShowViewModal(true);
            }}
          >
            👁️
          </button>
          {role === 'Admin' && (
            <button
              className="action-button edit"
              onClick={() => {
                setEditingCompany(company);
                setNewCompany({ ...company });
                setShowEditModal(true);
              }}
            >
              ✏️
            </button>
          )}
        </div>
      ),
    },
  ];

  // --- Render ---
  return (
    <div className="companies-tab">
      {/* Toolbar mit Suchfeldern und Buttons */}
      <div className="toolbar">
        <SearchBar fields={searchFields} />
        {role === 'Admin' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button
              className="add-button"
              onClick={() => {
                setNewCompany({
                  name: '',
                  sector: '',
                  address: '',
                  phone: '',
                  email: '',
                  website: '',
                  remarks: '',
                  highlighted: false,
                  contacted: false,
                  verified: false,
                  isExtern: false,
                });
                setShowAddModal(true);
              }}
            >
              ➕ Neues Unternehmen
            </button>
            <button
              className="export-button"
              onClick={exportToCSV}
            >
              📥 Export (CSV)
            </button>
            <strong>ausgewählt: {filteredCompanies.length}</strong>
          </div>
        )}
      </div>

      {/* Tabelle oder Lade-/Fehlermeldung */}
      {loading ? (
        <p>Lade Unternehmen...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <DataTable columns={columns} data={filteredCompanies} keyField="id" />
      )}

      {/* Modal: Neues Unternehmen */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Neues Unternehmen"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowAddModal(false)}>
              Abbrechen
            </button>
            <button type="submit" className="save-button" onClick={handleAddCompany}>
              Speichern
            </button>
          </div>
        }
      >
        <form onSubmit={handleAddCompany}>
          <CompanyForm data={newCompany} onChange={setNewCompany} />
        </form>
      </Modal>

      {/* Modal: Unternehmen bearbeiten */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Unternehmen bearbeiten"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowEditModal(false)}>
              Abbrechen
            </button>
            <button type="submit" className="save-button" onClick={handleEditCompany}>
              Speichern
            </button>
          </div>
        }
      >
        <form onSubmit={handleEditCompany}>
          <CompanyForm data={newCompany} onChange={setNewCompany} />
        </form>
      </Modal>

      {/* Modal: Unternehmen anzeigen */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={viewCompany?.name || ''}
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowViewModal(false)}>
              Schließen
            </button>
          </div>
        }
      >
        {viewCompany && (
          <div className="view-modal-content">
            <p><strong>Branche:</strong> {viewCompany.sector || 'Nicht angegeben'}</p>
            <p><strong>Adresse:</strong> {viewCompany.address || 'Nicht angegeben'}</p>
            <p><strong>Telefon:</strong> {viewCompany.phone || 'Nicht angegeben'}</p>
            <p><strong>E-Mail:</strong> {viewCompany.email || 'Nicht angegeben'}</p>
            <p><strong>Website:</strong> {viewCompany.website || 'Nicht angegeben'}</p>
            <p><strong>Bemerkungen:</strong> {viewCompany.remarks || 'Keine'}</p>
            <p><strong>Status:</strong> {viewCompany.active ? 'Aktiv' : 'Inaktiv'}</p>
            <p><strong>Kontaktiert:</strong> {viewCompany.contacted ? 'Ja' : 'Nein'}</p>
            <p><strong>Hervorgehoben:</strong> {viewCompany.highlighted ? 'Ja' : 'Nein'}</p>
            <p><strong>Verifiziert:</strong> {viewCompany.verified ? 'Ja' : 'Nein'}</p>
            <p><strong>Ext. Vertrieb:</strong> {viewCompany.isExtern ? 'Ja' : 'Nein'}</p>
          </div>
        )}
      </Modal>

      {/* Modal: Unternehmen deaktivieren */}
      <ConfirmModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={() => {
          handleDeactivateCompany(selectedCompanyId);
          setShowDeactivateModal(false);
        }}
        title="Unternehmen deaktivieren"
        message={
          <>
            Möchten Sie das Unternehmen <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong> wirklich deaktivieren?
            <br />
            <strong>Alle zugehörigen Ansprechpartner und Aufgaben werden automatisch deaktiviert/abgeschlossen.</strong>
          </>
        }
        confirmText="Ja, deaktivieren"
      />

      {/* Modal: Status ändern */}
      <ConfirmModal
        isOpen={showToggleStatusModal}
        onClose={() => setShowToggleStatusModal(false)}
        onConfirm={() => handleToggleStatus(selectedCompanyForToggle.id, selectedCompanyForToggle.currentStatus)}
        title="Status ändern"
        message={
          <>
            Möchten Sie den Status des Unternehmens <strong>{companies.find(c => c.id === selectedCompanyForToggle?.id)?.name}</strong>
            wirklich auf <strong>{selectedCompanyForToggle?.currentStatus ? 'Inaktiv' : 'Aktiv'}</strong> ändern?
          </>
        }
        confirmText="Ja, ändern"
      />

      {/* Modal: Fehlermeldung */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Fehler"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowErrorModal(false)}>
              OK
            </button>
          </div>
        }
      >
        <p>{errorModalMessage}</p>
      </Modal>
    </div>
  );
};

export default Companies;